import type { CSSProperties } from 'react'

export type TextLogoProps = {
  /** Escape hatch for consumer positioning (margin, cursor, etc.) — not used for core lockup layout. */
  className?: string
  /** Escape hatch for consumer style overrides — merged on top of the logo's own inline styles. */
  style?: CSSProperties
  /** Base font size in px for the primary wordmark */
  size?: number
  'aria-label'?: string
  /** When true, hides this wordmark from assistive tech (for nested lockup parts). */
  decorative?: boolean
}

/** Accessible props for standalone vs nested lockup wordmarks. */
export function textLogoA11yProps(
  ariaLabel: string,
  decorative?: boolean,
): { 'aria-hidden': true } | { 'aria-label': string; role: 'img' } {
  if (decorative) return { 'aria-hidden': true }
  return { 'aria-label': ariaLabel, role: 'img' }
}

/**
 * Every layout-critical property below (display, alignment, gap, weight, tracking,
 * line-height) is applied via inline style rather than Tailwind classes. This package
 * is shared across `app/` and `chrome-extension/`, which have separate Tailwind
 * configs whose `content` globs do not scan `packages/brand-logos/src`. A Tailwind
 * utility used only here (especially arbitrary-value classes like `gap-[0.35em]`)
 * silently produces no CSS in either build. Inline styles render identically no
 * matter which app imports the component or how its bundler is configured.
 */
const baseWordStyle: CSSProperties = {
  fontFamily: 'var(--font-logo)',
  display: 'inline-block',
  lineHeight: 1,
  letterSpacing: '-0.01em',
  whiteSpace: 'nowrap',
  userSelect: 'none',
}

/** Standard body text color: black in light mode, white in dark mode. */
export function logoPrimaryTextStyle(size: number, weight = 600): CSSProperties {
  return {
    ...baseWordStyle,
    fontSize: size,
    fontWeight: weight,
    color: 'hsl(var(--text-primary))',
  }
}

/** Brand gradient wordmark text. */
export function logoGradientTextStyle(size: number, weight = 600): CSSProperties {
  return {
    ...baseWordStyle,
    fontSize: size,
    fontWeight: weight,
    backgroundImage:
      'linear-gradient(90deg, hsl(var(--logo-gradient-1)) 0%, hsl(var(--logo-gradient-2)) 15%, hsl(var(--logo-gradient-3)) 30%, hsl(var(--logo-gradient-4)) 45%, hsl(var(--logo-gradient-5)) 60%, hsl(var(--logo-gradient-6)) 75%, hsl(var(--logo-gradient-7)) 90%, hsl(var(--logo-gradient-8)) 100%)',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    color: 'transparent',
  }
}
export function UniquelyNDGradientTextStyle(size: number, weight = 600): CSSProperties {
  return {
    ...baseWordStyle,
    fontSize: size,
    fontWeight: weight,
    backgroundImage:
      'linear-gradient(135deg, hsl(var(--logo-gradient-1)) 0%, hsl(var(--logo-gradient-2)) 15%, hsl(var(--logo-gradient-3)) 30%, hsl(var(--logo-gradient-4)) 45%, hsl(var(--logo-gradient-5)) 60%, hsl(var(--logo-gradient-6)) 75%, hsl(var(--logo-gradient-7)) 90%, hsl(var(--logo-gradient-8)) 100%)',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    color: 'transparent',
  }
}
/** Vertical divider bar used in two-word lockups (e.g. "Uniquely | ND"). */
export function logoDividerStyle(size: number): CSSProperties {
  return {
    display: 'inline-block',
    width: 1.5,
    height: size * 0.85,
    borderRadius: 1,
    backgroundColor: 'hsl(var(--text-primary)/0.75)',
    flexShrink: 0,
  }
}

/** Side-by-side lockup container (e.g. "Uniquely | ND", "LINA | LENS"). */
export function logoRowStyle(gapEm = 0.25): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: `${gapEm}em`,
    lineHeight: 1,
  }
}

/** Stacked lockup container (e.g. "ask" over "LINA"). */
export function logoColumnStyle(): CSSProperties {
  return {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    lineHeight: 1,
  }
}
